import axios from 'axios';

// API base URL - detect production automatically
// Check if running in production (not localhost)
const isLocalhost = window.location.hostname === 'localhost' ||
                    window.location.hostname === '127.0.0.1' ||
                    window.location.hostname === '';
const isProduction = !isLocalhost;

const API_URL = isProduction
  ? 'https://tml-golf-app.onrender.com/api'
  : '/api';

// Backend base URL (without /api) for serving static files
export const BACKEND_URL = isProduction
  ? 'https://tml-golf-app.onrender.com'
  : 'http://localhost:5001';

console.log('API_URL configured as:', API_URL);
console.log('BACKEND_URL configured as:', BACKEND_URL);

/**
 * Helper function to get full URL for images
 * - /uploads/* paths → backend (Render) for user-uploaded files
 * - /images/* paths → frontend (Vercel) for static assets
 * - http(s):// paths → used as-is
 */
export function getImageUrl(relativePath) {
  if (!relativePath) return null;
  if (relativePath.startsWith('http')) return relativePath; // Already full URL

  if (relativePath.startsWith('/uploads')) {
    // User-uploaded files on backend (ephemeral - not recommended for production)
    const fullUrl = `${BACKEND_URL}${relativePath}`;
    console.log('[getImageUrl] Backend upload:', relativePath, '→', fullUrl);
    return fullUrl;
  }

  if (relativePath.startsWith('/images')) {
    // Static assets served by frontend (Vercel)
    console.log('[getImageUrl] Frontend static:', relativePath);
    return relativePath; // Served from same domain
  }

  return relativePath;
}

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  console.log('[API] Request interceptor - method:', config.method, 'url:', config.url);
  return config;
}, (error) => {
  console.error('[API] Request interceptor error:', error);
  return Promise.reject(error);
});

// Handle response errors globally
api.interceptors.response.use(
  (response) => {
    console.log('[API] Response received - status:', response.status, 'url:', response.config.url);
    return response;
  },
  (error) => {
    console.error('[API] Response error:', error.message, 'url:', error.config?.url);
    // Handle both 401 (unauthorized) and 403 (forbidden/expired token)
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Token expired or invalid - clear auth
      localStorage.removeItem('authToken');
      window.dispatchEvent(new Event('auth-expired'));

      // If it's a token expiration, show a helpful message
      if (error.response?.data?.message) {
        console.warn('Auth error:', error.response.data.message);
      }
    }
    return Promise.reject(error);
  }
);

// ============= Auth API =============

export const authAPI = {
  login: async (password) => {
    const response = await api.post('/auth/login', { password });
    return response.data;
  },

  validate: async () => {
    const response = await api.post('/auth/validate');
    return response.data;
  }
};

// ============= Players API =============

export const playersAPI = {
  getAll: async (series = null) => {
    const params = series ? { series } : {};
    const response = await api.get('/players', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/players/${id}`);
    return response.data;
  },

  getWithRounds: async (id) => {
    // Gets player details with full round history and scores
    const response = await api.get(`/players/${id}`);
    return response.data;
  },

  create: async (playerData) => {
    const response = await api.post('/players', playerData);
    return response.data;
  },

  update: async (id, playerData) => {
    const response = await api.put(`/players/${id}`, playerData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/players/${id}`);
    return response.data;
  },

  uploadPhoto: async (id, photoFile) => {
    const formData = new FormData();
    formData.append('photo', photoFile);

    const response = await api.post(`/players/${id}/photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }
};

// ============= Members API =============
// Members are editorial profiles separate from players (scoring roster)

export const membersAPI = {
  getAll: async () => {
    const response = await api.get('/members');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/members/${id}`);
    return response.data;
  },

  create: async (memberData) => {
    const response = await api.post('/members', memberData);
    return response.data;
  },

  update: async (id, memberData) => {
    const response = await api.put(`/members/${id}`, memberData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/members/${id}`);
    return response.data;
  },

  uploadPhoto: async (id, photoFile) => {
    const formData = new FormData();
    formData.append('photo', photoFile);

    const response = await api.post(`/members/${id}/photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }
};

// ============= Courses API =============

export const coursesAPI = {
  getAll: async () => {
    const response = await api.get('/courses');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/courses/${id}`);
    return response.data;
  },

  create: async (courseData) => {
    const response = await api.post('/courses', courseData);
    return response.data;
  },

  update: async (id, courseData) => {
    const response = await api.put(`/courses/${id}`, courseData);
    return response.data;
  },

  updateHole: async (courseId, holeNumber, holeData) => {
    const response = await api.put(`/courses/${courseId}/holes/${holeNumber}`, holeData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/courses/${id}`);
    return response.data;
  },

  parseScorecardImage: async (imageFile) => {
    const formData = new FormData();
    formData.append('scorecard', imageFile);

    const response = await api.post('/courses/parse-scorecard', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000 // 60 seconds for AI processing
    });
    return response.data;
  }
};

// ============= Rounds API =============

export const roundsAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await api.get(`/rounds?${params}`);
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/rounds/${id}`);
    return response.data;
  },

  getActive: async () => {
    const response = await api.get('/rounds/active');
    return response.data;
  },

  getLeaderboard: async (id) => {
    const response = await api.get(`/rounds/${id}/leaderboard`);
    return response.data;
  },

  create: async (roundData) => {
    const response = await api.post('/rounds', roundData);
    return response.data;
  },

  createEmptyRound: async (roundData) => {
    const response = await api.post('/rounds/empty', roundData);
    return response.data;
  },

  assignPlayers: async (id, players) => {
    const response = await api.post(`/rounds/${id}/assign-players`, { players }, { timeout: 60000 });
    return response.data;
  },

  start: async (id) => {
    const response = await api.put(`/rounds/${id}/start`);
    return response.data;
  },

  updateStatus: async (id, status) => {
    const response = await api.put(`/rounds/${id}/status`, { status });
    return response.data;
  },

  complete: async (id) => {
    const response = await api.put(`/rounds/${id}/complete`);
    return response.data;
  },

  resolveTie: async (id, winnerId) => {
    const response = await api.post(`/rounds/${id}/resolve-tie`, { winnerId });
    return response.data;
  },

  getResults: async (id) => {
    const response = await api.get(`/rounds/${id}/results`);
    return response.data;
  },

  getPlayoffStatus: async (id) => {
    const response = await api.get(`/rounds/${id}/playoff-status`);
    return response.data;
  },

  setPlayoffWinner: async (id, playerId, type) => {
    const response = await api.post(`/rounds/${id}/playoff-winner`, { player_id: playerId, type });
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/rounds/${id}`);
    return response.data;
  }
};

// ============= Scores API =============

export const scoresAPI = {
  getByRoundPlayer: async (roundPlayerId) => {
    const response = await api.get(`/scores/round-player/${roundPlayerId}`);
    return response.data;
  },

  getByHole: async (roundPlayerId, holeNumber) => {
    const response = await api.get(`/scores/round-player/${roundPlayerId}/hole/${holeNumber}`);
    return response.data;
  },

  getScorecard: async (roundPlayerId) => {
    const response = await api.get(`/scores/scorecard/${roundPlayerId}`);
    return response.data;
  },

  submit: async (scoreData) => {
    const response = await api.post('/scores', scoreData);
    return response.data;
  },

  clear: async (roundPlayerId, holeNumber) => {
    const response = await api.delete(`/scores/round-player/${roundPlayerId}/hole/${holeNumber}`);
    return response.data;
  }
};

// ============= History API =============

export const historyAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await api.get(`/history?${params}`);
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/history/${id}`);
    return response.data;
  },

  getByYear: async (year) => {
    const response = await api.get(`/history/year/${year}`);
    return response.data;
  },

  create: async (historyData) => {
    const response = await api.post('/history', historyData);
    return response.data;
  },

  update: async (id, historyData) => {
    const response = await api.put(`/history/${id}`, historyData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/history/${id}`);
    return response.data;
  },

  uploadImage: async (id, imageFile) => {
    const formData = new FormData();
    formData.append('image', imageFile);

    const response = await api.post(`/history/${id}/upload-image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }
};

export const fridaveAPI = {
  getStandings: async (season) => {
    const response = await api.get(`/fridave/standings?season=${season}`);
    return response.data;
  },

  getRounds: async (season) => {
    const response = await api.get(`/fridave/rounds?season=${season}`);
    return response.data;
  },

  getPlayerRounds: async (playerId, season) => {
    const response = await api.get(`/fridave/standings/${playerId}?season=${season}`);
    return response.data;
  },

  getSeasons: async () => {
    const response = await api.get('/fridave/seasons');
    return response.data;
  },

  getEclectic: async (season) => {
    const response = await api.get(`/fridave/eclectic?season=${season}`);
    return response.data;
  },

  getEclecticScorecard: async (playerId, season) => {
    const response = await api.get(`/fridave/eclectic/${playerId}?season=${season}`);
    return response.data;
  },
};

export default api;
